export type BulkOverwriteMode = 'empty_only' | 'all';

export type BulkOverwriteFieldKey =
    | 'abgangsgewicht'
    | 'einstallgewicht'
    | 'kp_abgangspreis'
    | 'kp_einstallpreis';

export interface BulkOverwriteFieldSpec {
    key: BulkOverwriteFieldKey;
    label: string;
    /** List total the user entered (Gesamt). */
    gesamtValue?: number | null;
    /** Explicit per-animal value (Pro Tier), if known. */
    proTierValue?: number | null;
    /**
     * true = user edited „pro Tier“ → empty_only writes that value as-is.
     * false/omit = treat gesamt as source of truth → empty_only = (gesamt − Summe vorhandener) ÷ leere.
     */
    preferProTier?: boolean;
    unit?: string;
}

export interface BulkOverwriteAnimalRow {
    ohrmarke: string;
    einstallgewicht?: number | null;
    ausstallgewicht?: number | null;
    kp_einstallpreis?: number | null;
    kp_abgangspreis?: number | null;
}

export interface BulkOverwriteFieldStat {
    key: BulkOverwriteFieldKey;
    label: string;
    unit?: string;
    total: number;
    filled: number;
    empty: number;
    filledSum: number;
    /** Value written if user chooses „Alle überschreiben“. */
    perTierAll: number | null;
    /** Value written to empty animals if „Nur leere befüllen“. */
    perTierEmptyOnly: number | null;
    /** Remaining total for empty_only when distributing from gesamt. */
    remainingForEmpty: number | null;
}

export interface BulkOverwriteDialogInput {
    fields: BulkOverwriteFieldSpec[];
    animals: BulkOverwriteAnimalRow[];
}

export interface BulkOverwriteDialogResult {
    mode: BulkOverwriteMode;
}

export function getAnimalFieldValue(
    animal: BulkOverwriteAnimalRow,
    key: BulkOverwriteFieldKey,
): number | null | undefined {
    switch (key) {
        case 'abgangsgewicht':
            return animal.ausstallgewicht;
        case 'einstallgewicht':
            return animal.einstallgewicht;
        case 'kp_abgangspreis':
            return animal.kp_abgangspreis;
        case 'kp_einstallpreis':
            return animal.kp_einstallpreis;
        default:
            return null;
    }
}

/** Weight: null/0 = empty. Price: null only (0 is still a set value). */
export function animalHasFieldValue(animal: BulkOverwriteAnimalRow, key: BulkOverwriteFieldKey): boolean {
    const v = getAnimalFieldValue(animal, key);
    if (v == null || Number.isNaN(Number(v))) {
        return false;
    }
    if (key === 'abgangsgewicht' || key === 'einstallgewicht') {
        return Number(v) > 0;
    }
    return true;
}

export function sumFilledFieldValues(animals: BulkOverwriteAnimalRow[], key: BulkOverwriteFieldKey): number {
    let sum = 0;
    for (const a of animals ?? []) {
        if (!animalHasFieldValue(a, key)) {
            continue;
        }
        sum += Number(getAnimalFieldValue(a, key)) || 0;
    }
    return sum;
}

export function countEmptyAnimals(animals: BulkOverwriteAnimalRow[], key: BulkOverwriteFieldKey): number {
    return (animals ?? []).filter(a => !animalHasFieldValue(a, key)).length;
}

function roundMoneyOrKg(v: number): number {
    return Math.round(v * 100) / 100;
}

/**
 * Per-animal value to POST for a given overwrite mode.
 * - all: gesamt/n or explicit proTier
 * - empty_only + preferProTier: proTier as-is
 * - empty_only + gesamt: (gesamt − Summe vorhandener) / Anzahl leerer
 */
export function resolveBulkPerTierValue(
    mode: BulkOverwriteMode,
    animals: BulkOverwriteAnimalRow[],
    field: BulkOverwriteFieldSpec,
): number | null {
    const total = animals?.length ?? 0;
    const empty = countEmptyAnimals(animals, field.key);
    const filledSum = sumFilledFieldValues(animals, field.key);
    const gesamt = field.gesamtValue != null ? Number(field.gesamtValue) : null;
    const proTier = field.proTierValue != null ? Number(field.proTierValue) : null;

    if (mode === 'all') {
        if (gesamt != null && gesamt > 0 && total > 0) {
            return roundMoneyOrKg(gesamt / total);
        }
        if (proTier != null && proTier > 0) {
            return roundMoneyOrKg(proTier);
        }
        return null;
    }

    // empty_only
    if (empty <= 0) {
        return null;
    }
    if (field.preferProTier && proTier != null && proTier > 0) {
        return roundMoneyOrKg(proTier);
    }
    if (gesamt != null && gesamt > 0) {
        const remaining = gesamt - filledSum;
        if (remaining <= 0) {
            return null;
        }
        return roundMoneyOrKg(remaining / empty);
    }
    if (proTier != null && proTier > 0) {
        return roundMoneyOrKg(proTier);
    }
    return null;
}

export function buildBulkOverwriteFieldStats(
    animals: BulkOverwriteAnimalRow[],
    fields: BulkOverwriteFieldSpec[],
): BulkOverwriteFieldStat[] {
    const total = animals?.length ?? 0;
    return (fields ?? []).map(f => {
        let filled = 0;
        for (const a of animals ?? []) {
            if (animalHasFieldValue(a, f.key)) {
                filled += 1;
            }
        }
        const empty = Math.max(0, total - filled);
        const filledSum = sumFilledFieldValues(animals, f.key);
        const gesamt = f.gesamtValue != null ? Number(f.gesamtValue) : null;
        const remaining =
            gesamt != null && gesamt > 0 && !f.preferProTier ? roundMoneyOrKg(gesamt - filledSum) : null;
        return {
            key: f.key,
            label: f.label,
            unit: f.unit,
            total,
            filled,
            empty,
            filledSum: roundMoneyOrKg(filledSum),
            perTierAll: resolveBulkPerTierValue('all', animals, f),
            perTierEmptyOnly: resolveBulkPerTierValue('empty_only', animals, f),
            remainingForEmpty: remaining,
        };
    });
}

export function needsBulkOverwriteConfirm(
    animals: BulkOverwriteAnimalRow[],
    fields: BulkOverwriteFieldSpec[],
): boolean {
    if (!animals?.length || !fields?.length) {
        return false;
    }
    return fields.some(f => animals.some(a => animalHasFieldValue(a, f.key)));
}

export function mapGruppeTierRecordToBulkRow(rec: Record<string, unknown>): BulkOverwriteAnimalRow {
    const ohrmarke =
        (rec['ohrmarke_notnull'] as string) ||
        (rec['ohrmarke'] as string) ||
        (rec['ohrmarke15'] as string) ||
        (rec['ohrmarke_kurz'] as string) ||
        '–';
    const num = (k: string): number | null => {
        const v = rec[k];
        if (v == null || v === '') {
            return null;
        }
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
    };
    return {
        ohrmarke,
        einstallgewicht: num('einstallgewicht'),
        ausstallgewicht: num('ausstallgewicht') ?? num('abgangsgewicht'),
        kp_einstallpreis: num('kp_einstallpreis'),
        kp_abgangspreis: num('kp_abgangspreis'),
    };
}
